import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { invokeFunctionSilent } from '@/lib/cloudrun';
import { generateJitsiMeetingUrl, generateJitsiRoomName } from "@/utils/jitsi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, Users, Video, Loader2, Copy, Check, Mail } from "lucide-react";
import { format, addHours, setHours, setMinutes } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface ScheduleMeetingFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerType?: string;
  onMeetingCreated?: () => void;
}

const translations = {
  en: {
    title: "Schedule New Meeting",
    subtitle: "Set up a video meeting with your clients",
    meetingTitle: "Meeting Title",
    meetingTitlePlaceholder: "e.g., Initial Consultation",
    description: "Description (optional)",
    descriptionPlaceholder: "Meeting agenda or notes...",
    selectDate: "Select Date",
    pickDate: "Pick a date",
    selectTime: "Time",
    duration: "Duration",
    attendeeEmail: "Attendee Email",
    attendeePlaceholder: "client@example.com",
    addAttendee: "Add",
    attendees: "Attendees",
    noAttendees: "No attendees added",
    meetingType: "Meeting Type",
    videoCall: "Video Call (Jitsi)",
    phoneCall: "Phone Call",
    inPerson: "In Person",
    cancel: "Cancel",
    schedule: "Schedule Meeting",
    scheduling: "Scheduling...",
    success: "Meeting Scheduled",
    successDesc: "Your meeting has been scheduled successfully",
    error: "Error",
    errorDesc: "Failed to schedule meeting",
    copyLink: "Copy Link",
    linkCopied: "Link copied!",
    minutes: "minutes",
    hour: "hour",
    hours: "hours",
  },
  fr: {
    title: "Planifier une Réunion",
    subtitle: "Organisez une réunion vidéo avec vos clients",
    meetingTitle: "Titre de la Réunion",
    meetingTitlePlaceholder: "ex., Consultation Initiale",
    description: "Description (optionnel)",
    descriptionPlaceholder: "Ordre du jour ou notes...",
    selectDate: "Sélectionner Date",
    pickDate: "Choisir une date",
    selectTime: "Heure",
    duration: "Durée",
    attendeeEmail: "Email du Participant",
    attendeePlaceholder: "client@example.com",
    addAttendee: "Ajouter",
    attendees: "Participants",
    noAttendees: "Aucun participant ajouté",
    meetingType: "Type de Réunion",
    videoCall: "Appel Vidéo (Jitsi)",
    phoneCall: "Appel Téléphonique",
    inPerson: "En Personne",
    cancel: "Annuler",
    schedule: "Planifier",
    scheduling: "Planification...",
    success: "Réunion Planifiée",
    successDesc: "Votre réunion a été planifiée avec succès",
    error: "Erreur",
    errorDesc: "Échec de la planification",
    copyLink: "Copier le Lien",
    linkCopied: "Lien copié!",
    minutes: "minutes",
    hour: "heure",
    hours: "heures",
  },
};

const timeSlots = Array.from({ length: 20 }, (_, i) => {
  const hour = Math.floor(i / 2) + 8; // 8:00 AM to 5:30 PM
  const minute = (i % 2) * 30;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
});

const durationOptions = [
  { value: "15", label: { en: "15 minutes", fr: "15 minutes" } },
  { value: "30", label: { en: "30 minutes", fr: "30 minutes" } },
  { value: "45", label: { en: "45 minutes", fr: "45 minutes" } },
  { value: "60", label: { en: "1 hour", fr: "1 heure" } },
  { value: "90", label: { en: "1.5 hours", fr: "1,5 heures" } },
  { value: "120", label: { en: "2 hours", fr: "2 heures" } },
];

export function ScheduleMeetingForm({ 
  open, 
  onOpenChange, 
  partnerType,
  onMeetingCreated 
}: ScheduleMeetingFormProps) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: undefined as Date | undefined,
    time: "09:00",
    duration: "60",
    meetingType: "video",
    attendeeEmail: "",
    attendees: [] as string[],
  });
  const [generatedLink, setGeneratedLink] = useState<string>("");

  const handleAddAttendee = () => {
    const email = formData.attendeeEmail.trim();
    if (email && !formData.attendees.includes(email)) {
      setFormData({
        ...formData,
        attendees: [...formData.attendees, email],
        attendeeEmail: "",
      });
    }
  };

  const handleRemoveAttendee = (email: string) => {
    setFormData({
      ...formData,
      attendees: formData.attendees.filter(a => a !== email),
    });
  };

  const handleSubmit = async () => {
    if (!user || !formData.title || !formData.date) {
      toast.error(t.error);
      return;
    }

    setIsSubmitting(true);
    try {
      // Create meeting date/time
      const [hours, minutes] = formData.time.split(':').map(Number);
      const scheduledAt = setMinutes(setHours(formData.date, hours), minutes);

      // Generate Jitsi link for video calls
      let meetingLink = "";
      if (formData.meetingType === "video") {
        const roomName = generateJitsiRoomName(formData.title);
        meetingLink = generateJitsiMeetingUrl(roomName);
        setGeneratedLink(meetingLink);
      }

      // Save to Firestore
      await addDoc(collection(db, 'partnerMeetings'), {
        partnerId: user.email,
        partnerType: partnerType || 'general',
        title: formData.title,
        description: formData.description,
        scheduledAt,
        duration: parseInt(formData.duration),
        meetingType: formData.meetingType,
        meetingLink,
        attendees: [...formData.attendees, user.email],
        status: 'scheduled',
        createdAt: serverTimestamp(),
      });

      // Send email confirmations to all attendees
      if (formData.attendees.length > 0) {
        try {
          const dateLocale = language === 'fr' ? fr : undefined;
          const formattedDate = format(scheduledAt, 'PPPP', { locale: dateLocale });
          const formattedTime = format(scheduledAt, 'HH:mm');

          await invokeFunctionSilent('send-meeting-confirmation', {
organizerName: user.displayName || user.email?.split('@')[0] || 'Organizer',
organizerEmail: user.email,
meetingTitle: formData.title,
meetingDescription: formData.description || undefined,
meetingDate: formattedDate,
meetingTime: formattedTime,
duration: parseInt(formData.duration),
meetingType: formData.meetingType,
 meetingLink: meetingLink || undefined,
attendeeEmails: formData.attendees,
language: language,
});
          
          toast.success(
            language === 'fr' ? 'Invitations envoyées' : 'Invitations sent',
            { description: language === 'fr' ? 'Les participants ont été notifiés par email' : 'Attendees have been notified by email' }
          );
        } catch (emailError) {
          console.error('Error sending meeting emails:', emailError);
          // Don't fail the whole operation if email fails
        }
      }

      toast.success(t.success, { description: t.successDesc });
      
      // Reset form
      setFormData({
        title: "",
        description: "",
        date: undefined,
        time: "09:00",
        duration: "60",
        meetingType: "video",
        attendeeEmail: "",
        attendees: [],
      });

      onMeetingCreated?.();
      
      // Keep dialog open if there's a link to copy
      if (!meetingLink) {
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast.error(t.error, { description: t.errorDesc });
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyMeetingLink = async () => {
    if (generatedLink) {
      await navigator.clipboard.writeText(generatedLink);
      setLinkCopied(true);
      toast.success(t.linkCopied);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setGeneratedLink("");
    setLinkCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.subtitle}</DialogDescription>
        </DialogHeader>

        {generatedLink ? (
          // Success state with link
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-center font-medium">{t.success}</p>
            <div className="flex items-center gap-2">
              <Input 
                value={generatedLink} 
                readOnly 
                className="flex-1 text-sm"
              />
              <Button onClick={copyMeetingLink} variant="outline" size="icon">
                {linkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={handleClose} className="w-full">
                {language === 'fr' ? 'Fermer' : 'Close'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // Form state
          <div className="space-y-4 py-4">
            {/* Meeting Title */}
            <div className="space-y-2">
              <Label htmlFor="meeting-title">{t.meetingTitle}</Label>
              <Input
                id="meeting-title"
                placeholder={t.meetingTitlePlaceholder}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t.description}</Label>
              <Textarea
                id="description"
                placeholder={t.descriptionPlaceholder}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.selectDate}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date 
                        ? format(formData.date, "PPP", { locale: language === 'fr' ? fr : undefined })
                        : t.pickDate
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => setFormData({ ...formData, date })}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>{t.selectTime}</Label>
                <Select
                  value={formData.time}
                  onValueChange={(value) => setFormData({ ...formData, time: value })}
                >
                  <SelectTrigger>
                    <Clock className="mr-2 h-4 w-4" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Duration and Meeting Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.duration}</Label>
                <Select
                  value={formData.duration}
                  onValueChange={(value) => setFormData({ ...formData, duration: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {durationOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label[language as 'en' | 'fr']}
                      </SelectItem>
                    ))
                    }
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t.meetingType}</Label>
                <Select
                  value={formData.meetingType}
                  onValueChange={(value) => setFormData({ ...formData, meetingType: value })}
                >
                  <SelectTrigger>
                    <Video className="mr-2 h-4 w-4" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">{t.videoCall}</SelectItem>
                    <SelectItem value="phone">{t.phoneCall}</SelectItem>
                    <SelectItem value="inPerson">{t.inPerson}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Attendees */}
            <div className="space-y-2">
              <Label>{t.attendees}</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder={t.attendeePlaceholder}
                  value={formData.attendeeEmail}
                  onChange={(e) => setFormData({ ...formData, attendeeEmail: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAttendee())}
                />
                <Button type="button" variant="outline" onClick={handleAddAttendee}>
                  {t.addAttendee}
                </Button>
              </div>
              {formData.attendees.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.attendees.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-sm rounded-full"
                    >
                      <Users className="h-3 w-3" />
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveAttendee(email)}
                        className="ml-1 hover:text-destructive"
                      >
                        ×
                      </button>
                    </span>
                  ))
                  }
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t.noAttendees}</p>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={handleClose}>
                {t.cancel}
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting || !formData.title || !formData.date}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t.scheduling}
                  </>
                ) : (
                  <>
                    <Video className="mr-2 h-4 w-4" />
                    {t.schedule}
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
